// VOICE seams (Brief 3 D7) — dictation in, listening out, as INJECTED
// platform capabilities (the TokenStore/fetch precedent): this file defines
// the contracts + the platform-neutral control logic; each host supplies its
// engine (web: the browser's SpeechRecognition / speechSynthesis; RN: its
// own libraries later; a platform with neither simply shows no affordance).
// Nothing here touches `window` — the seams arrive from outside.
//
// The LISTEN side is deliberately seam-shaped so the local engine can later
// be swapped for the Fal voice the email reports use (a quality upgrade,
// config-gated) without touching any surface.

/** Speech-to-text: the dictation engine a platform provides. */
export interface DictationSeam {
  /** Whether an engine exists here (no engine ⇒ surfaces hide the mic). */
  available(): boolean;
  /**
   * Start listening. `onText` receives FINALIZED fragments as they land
   * (never interim guesses — the composer only appends settled text);
   * `onEnd` fires when the engine stops (user stop, silence, or error).
   * Returns a stop function (idempotent).
   */
  start(onText: (text: string) => void, onEnd: () => void): () => void;
}

/** Text-to-speech: the listening engine a platform provides. */
export interface ListenSeam {
  /** Whether an engine exists here (no engine ⇒ surfaces hide listen). */
  available(): boolean;
  /**
   * Speak `text` aloud. `onEnd` fires when playback finishes OR is
   * cancelled. Returns a cancel function (idempotent).
   */
  speak(text: string, onEnd: () => void): () => void;
}

/** The no-engine fallback: never available, never speaks/listens. Hosts use
 * it when a platform has neither engine, so surfaces degrade by hiding the
 * affordances rather than branching. */
export const NO_SPEECH: DictationSeam & ListenSeam = {
  available: () => false,
  start: () => () => {},
  speak: () => () => {},
};

/** Append a finalized dictation fragment to a draft: single-space joined,
 * fragment trimmed; an empty fragment is a no-op. PURE — the composer never
 * hand-assembles transcript text. */
export function appendDictation(draft: string, fragment: string): string {
  const text = fragment.trim();
  if (!text) return draft;
  if (draft === "" || draft.endsWith(" ") || draft.endsWith("\n")) {
    return draft + text;
  }
  return `${draft} ${text}`;
}

/** One-at-a-time playback over a [`ListenSeam`]: toggling the playing item
 * stops it; starting another cancels the first. `onChange` reports the
 * currently-playing key (null when silent) so a view re-renders its toggle
 * states. Engine-agnostic — unit-tested with a fake seam. */
export function createListenController(seam: ListenSeam): {
  available(): boolean;
  toggle(key: string, text: string, onChange: (playing: string | null) => void): void;
  stop(): void;
} {
  let cancel: (() => void) | null = null;
  let playingKey: string | null = null;

  const silence = (onChange?: (playing: string | null) => void) => {
    cancel?.();
    cancel = null;
    playingKey = null;
    onChange?.(null);
  };

  return {
    available: () => seam.available(),
    toggle(key, text, onChange) {
      if (playingKey === key) {
        // Toggling the playing item stops it.
        silence(onChange);
        return;
      }
      // Starting a new item cancels whatever was playing.
      cancel?.();
      playingKey = key;
      onChange(key);
      cancel = seam.speak(text, () => {
        // Natural end (or engine-side cancel): clear only if still current.
        if (playingKey === key) {
          cancel = null;
          playingKey = null;
          onChange(null);
        }
      });
    },
    stop() {
      silence();
    },
  };
}
